import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginRequest, RegisterRequest } from '../types';
import { authAPI } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest, resumeFile?: File) => Promise<{ message: string }>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  getAuthHeaders: () => Record<string, string>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isRecruiter: boolean;
  isCandidate: boolean;
  isPartner: boolean;
  isJobManagementAdmin: boolean;
  isBillingAdmin: boolean;
  isSuperAdmin: boolean;
  isOrgAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          setUser(JSON.parse(storedUser));
          
          // Verify token is still valid
          const response = await authAPI.getCurrentUser();
          if (response.user.is_deleted || response.user.is_active === false) {
            logout();
            return;
          }
          setUser(response.user);
          localStorage.setItem('user', JSON.stringify(response.user));
        } catch (error) {
          // Token is invalid, clear storage
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    let stopped = false;
    const ping = async () => {
      if (stopped) return;
      try {
        const result = await authAPI.heartbeat();
        // Keep the cached user in sync with server state so role/flag changes
        // (e.g. is_org_admin promote/demote, is_active suspend/resume) take effect
        // within the heartbeat interval without requiring a full page reload.
        if (result?.user) {
          setUser((prev) => {
            const next = result.user as User;
            if (!prev) return next;
            if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
            try {
              localStorage.setItem('user', JSON.stringify(next));
            } catch {
              // localStorage write failures are non-fatal.
            }
            return next;
          });
        }
      } catch {
        // Heartbeat should not interrupt user flow.
      }
    };

    ping();
    const intervalId = window.setInterval(ping, 30000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        ping();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stopped = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user?.id]);

  const login = async (credentials: LoginRequest) => {
    try {
      const response = await authAPI.login(credentials);
      
      setUser(response.user);
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      localStorage.setItem('user', JSON.stringify(response.user));
    } catch (error: any) {
      const err = new Error(error.response?.data?.error || 'Login failed') as Error & { status?: string };
      err.status = error.response?.data?.status;
      throw err;
    }
  };

  const register = async (userData: RegisterRequest, resumeFile?: File) => {
    try {
      const response = await authAPI.register(userData, resumeFile);
      return response;
    } catch (error: any) {
      // Fallback chain: parsed JSON error → parsed JSON message → parsed JSON details
      // → HTTP status text → axios error code → axios error message → generic.
      // Non-JSON responses (415 HTML, gateway timeouts) and network errors used to
      // collapse into the useless "Registration failed" string; this exposes the
      // real reason so users and support can act on it.
      const data = error?.response?.data;
      const responseStatus = error?.response?.status;
      const statusText = error?.response?.statusText;

      let message: string;
      if (data && typeof data === 'object') {
        message = data.error || data.message || data.details
          || (statusText ? `${responseStatus}: ${statusText}` : '')
          || error?.message
          || 'Registration failed';
      } else if (typeof data === 'string' && data.trim()) {
        // Some proxies return an HTML/text error page for 4xx/5xx. Show a
        // truncated snippet so we can tell it apart from an actual API error.
        const snippet = data.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
        message = snippet || (statusText ? `${responseStatus}: ${statusText}` : 'Registration failed');
      } else if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') {
        message = 'Network error — check your connection and try again.';
      } else if (statusText) {
        message = `${responseStatus}: ${statusText}`;
      } else {
        message = error?.message || 'Registration failed';
      }

      const err: any = new Error(message);
      err.code = data?.code;
      err.data = data;
      err.status = responseStatus;
      throw err;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    getAuthHeaders,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isRecruiter: user?.role === 'recruiter',
    isCandidate: user?.role === 'candidate',
    isPartner: user?.role === 'partner',
    isJobManagementAdmin: user?.role === 'admin' && user?.admin_title?.toLowerCase() === 'job management admin',
    isBillingAdmin: user?.role === 'admin' && user?.admin_title?.toLowerCase() === 'billing admin',
    isSuperAdmin: user?.role === 'admin' && (!user?.admin_title || user?.admin_title?.toLowerCase() === 'super admin'),
    isOrgAdmin: user?.role === 'recruiter' && !!user?.is_org_admin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
